import {
  getProofreadingManuscriptContent,
  getProofreadingWorkManuscript,
  listProofreadingWorkManuscripts,
} from '@/lib/api/proofreading';
import type {
  ProofreadingWorkManuscriptGetResult,
  RuleBasedManuscriptSource,
} from '@/lib/api/proofreading';
import type { Manuscript } from '@/lib/models';
import { pickBestWorkProgressData } from '@/lib/utils/proofreadingWorkProgress';
import type { WorkProgressDataV2 } from '@/lib/utils/proofreadingWorkProgress';

export type ProofreadingManuscriptItem = {
  key: string;
  title: string;
  content: string;
  /** work 詳細が取れた場合は `"work"`、原文フォールバックは `"content"`（rule-based API にそのまま渡せる）。 */
  source: RuleBasedManuscriptSource;
};

export type WorkManuscriptContent = {
  text: string;
  workProgressData: string | null;
};

export type PopulateWorkByKeyResult = {
  workByKey: Map<string, string>;
  workProgressByKey: Map<string, string>;
};

export async function populateWorkByKey(
  novelId: string,
  keys: string[],
  getWork: (novelId: string, manuscriptId: string) => Promise<ProofreadingWorkManuscriptGetResult>,
): Promise<PopulateWorkByKeyResult> {
  const workByKey = new Map<string, string>();
  const workProgressByKey = new Map<string, string>();

  await Promise.all(
    keys.map(async (key) => {
      const work = await getWork(novelId, key);
      if (work.exists) {
        workByKey.set(key, work.text);
        if (work.workProgressData != null && work.workProgressData !== '') {
          workProgressByKey.set(key, work.workProgressData);
        }
      }
    }),
  );

  return { workByKey, workProgressByKey };
}

function resolveProofreadingManuscriptContent(
  key: string,
  workByKey: Map<string, string>,
  apiContents: Map<string, string>,
): Pick<ProofreadingManuscriptItem, 'content' | 'source'> {
  const workContent = workByKey.get(key);
  if (workContent != null && workContent !== '') {
    return { content: workContent, source: 'work' };
  }

  const apiContent = apiContents.get(key);
  if (apiContent != null && apiContent !== '') {
    return { content: apiContent, source: 'content' };
  }

  return {
    content: '',
    source: 'content',
  };
}

export type LoadProofreadingManuscriptItemsResult = {
  items: ProofreadingManuscriptItem[];
  workProgressData: WorkProgressDataV2 | null;
  /** work 一覧 API の manuscriptIds が1件以上ある場合 true */
  hasExistingWork: boolean;
  /** work はあるが本文が空で、正規原稿 API から補完した場合 true */
  hasHydratableEmptyWork: boolean;
};

/**
 * 原稿 key の並びを揃える。
 * 1. selectedManuscriptKeys（ユーザーが選んだ順）
 * 2. manuscriptList（執筆画面の displayOrder 順）
 * 3. 上記に無い key（フォールバック）
 */
export function sortManuscriptKeys(params: {
  keys: string[];
  selectedManuscriptKeys: string[];
  manuscriptList: Manuscript[];
}): string[] {
  const { keys, selectedManuscriptKeys, manuscriptList } = params;
  const remaining = new Set(keys);
  const ordered: string[] = [];

  selectedManuscriptKeys.forEach((key) => {
    if (remaining.delete(key)) {
      ordered.push(key);
    }
  });

  manuscriptList.forEach((manuscript) => {
    const {key} = manuscript;
    if (key && remaining.delete(key)) {
      ordered.push(key);
    }
  });

  remaining.forEach((key) => ordered.push(key));

  return ordered;
}

/**
 * 校正作業用の原稿一覧を組み立てる。
 * 選択中原稿は work → 正規原稿（校正 REST API）の順で解決し、一覧 API にある未選択 work も末尾に加える。
 * work 詳細取得は一覧 API の manuscriptIds のみを対象とする（一覧にない key は 404 になるため）。
 * work_progress_data は各原稿から収集し、savedAt が最新のものを返す。
 */
function shouldIncludeUnselectedWorkItem(
  key: string,
  workByKey: Map<string, string>,
  apiContents: Map<string, string>,
  titleByKey: Map<string, string>,
): boolean {
  if (!titleByKey.has(key)) {
    return false;
  }

  const workContent = workByKey.get(key);
  if (workContent != null && workContent !== '') {
    return true;
  }

  const apiContent = apiContents.get(key);
  return apiContent != null && apiContent !== '';
}

export async function loadProofreadingManuscriptItems(params: {
  novelId: string;
  selectedManuscriptKeys: string[];
  manuscriptList: Manuscript[];
  /** false のとき未選択 work を末尾に足さない（AI 修正反映向け）。既定 true */
  includeUnselectedWork?: boolean;
}): Promise<LoadProofreadingManuscriptItemsResult> {
  const {
    novelId,
    selectedManuscriptKeys,
    manuscriptList,
    includeUnselectedWork = true,
  } = params;

  const { manuscriptIds } = await listProofreadingWorkManuscripts(novelId);
  const titleByKey = new Map<string, string>();
  manuscriptList.forEach((m) => {
    if (typeof m.key !== 'string' || m.key.length === 0) return;
    titleByKey.set(m.key, m.title ?? m.key);
  });
  const unselectedWorkKeys = manuscriptIds.filter((key) => !selectedManuscriptKeys.includes(key));

  const { workByKey, workProgressByKey } = await populateWorkByKey(
    novelId,
    manuscriptIds,
    getProofreadingWorkManuscript,
  );

  const keysNeedingApiContent = [...new Set([
    ...selectedManuscriptKeys,
    ...(includeUnselectedWork
      ? unselectedWorkKeys.filter((key) => titleByKey.has(key))
      : []),
  ])].filter((key) => {
    const workContent = workByKey.get(key);
    return workContent == null || workContent === '';
  });
  const apiContents = new Map<string, string>();

  if (keysNeedingApiContent.length > 0) {
    await Promise.all(
      keysNeedingApiContent.map(async (key) => {
        try {
          const content = await getProofreadingManuscriptContent(novelId, key, 'content');
          apiContents.set(key, content);
        } catch {
          apiContents.set(key, '');
        }
      }),
    );
  }

  const selectedItems: ProofreadingManuscriptItem[] = selectedManuscriptKeys.map((key) => {
    const { content, source } = resolveProofreadingManuscriptContent(
      key,
      workByKey,
      apiContents,
    );
    return {
      key,
      title: titleByKey.get(key) ?? key,
      content,
      source,
    };
  });

  const workOnlyItems: ProofreadingManuscriptItem[] = includeUnselectedWork
    ? unselectedWorkKeys
      .filter((key) => workByKey.has(key))
      .filter((key) => shouldIncludeUnselectedWorkItem(key, workByKey, apiContents, titleByKey))
      .map((key) => {
      const { content, source } = resolveProofreadingManuscriptContent(
        key,
        workByKey,
        apiContents,
      );
      return {
        key,
        title: titleByKey.get(key) ?? key,
        content,
        source,
      };
    })
    : [];

  const hasHydratableEmptyWork = manuscriptIds.some((key) => {
    const workContent = workByKey.get(key);
    if (workContent == null || workContent !== '') {
      return false;
    }
    const apiContent = apiContents.get(key);
    return apiContent != null && apiContent !== '';
  });

  const workProgressData = pickBestWorkProgressData([...workProgressByKey.values()]);

  const itemsByKey = new Map(
    [...selectedItems, ...workOnlyItems].map((item) => [item.key, item] as const),
  );
  const orderedKeys = sortManuscriptKeys({
    keys: [...itemsByKey.keys()],
    selectedManuscriptKeys,
    manuscriptList,
  });

  return {
    items: orderedKeys.map((key) => itemsByKey.get(key)!),
    workProgressData,
    hasExistingWork: manuscriptIds.length > 0,
    hasHydratableEmptyWork,
  };
}
